// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * WebSocket message handlers extracted from WebSocketContext.
 *
 * Uses useGameStore.getState() to read fresh state on each message,
 * eliminating stale closure risks from subscribing to store values.
 */

import { useCallback } from 'react';
import { applyPatch, type Operation } from 'fast-json-patch';
import { toast } from 'sonner';
import { logger } from '../lib/logger';
import { useChatHandler } from '../lib/useChatHandler';
import { useGameStore } from '../store/gameStore';
import { useSettingsStore } from '../store/settingsStore';
import { useSpectatorStore } from '../store/spectatorStore';
import { useTimerStore } from '../store/timerStore';
import type { LobbyState, GameState } from '../types/game';
import type { ServerPayloadMap } from '../types/messages';
import type {
  TimerStartPayload,
  TimerTickPayload,
  TimerCompletePayload,
  TimerPausedPayload,
  TimerResumedPayload,
  TimerCancelledPayload,
} from '../types/timer';

// Type for game state with player arrays
type StateWithPlayers = {
  current_players?: { player_id: string }[];
  all_players?: { player_id: string }[];
  room_status?: string;
  phase?: string;
};

type WebSocketMessage = {
  action: string;
  payload: Record<string, unknown>;
};

type GameStateUpdateEnvelope = {
  state?: GameState | LobbyState;
  sequence?: number;
  type?: 'full';
} & Record<string, unknown>;

type GameStateDeltaPayload = {
  patch: Array<Record<string, unknown>>;
  sequence: number;
  base_sequence: number;
  type?: 'delta';
};

interface UseMessageHandlersParams {
  navigate: (path: string) => void;
  getLocationPathname: () => string;
  requestResync: () => void;
}

// --- Plain handler functions (not hooks) ---

function handleGameStateUpdate(
  payload: Record<string, unknown>,
  navigate: (path: string) => void,
  locationPathname: string
): void {
  const store = useGameStore.getState();
  const wrappedPayload = payload as GameStateUpdateEnvelope;
  const actualState = (wrappedPayload.state || payload) as StateWithPlayers;
  const sequence =
    typeof wrappedPayload.sequence === 'number' ? wrappedPayload.sequence : undefined;

  logger.debug('ws_game_state_update_received', {
    hasState: 'state' in payload,
    room_status: actualState.room_status,
    phase: actualState.phase,
  });

  // Debug logging for PDQ player_phase sync issues
  const stateWithPhase = actualState as {
    player_phase?: string;
    dispute?: unknown;
  };
  if (stateWithPhase.player_phase || stateWithPhase.dispute) {
    logger.info('ws_player_phase_update', {
      player_phase: stateWithPhase.player_phase,
      hasDispute: 'dispute' in stateWithPhase,
      disputeActive: (stateWithPhase.dispute as { active?: boolean })?.active,
    });
  }

  store.updateGameState(actualState as GameState | LobbyState, sequence);

  // Check if game has started (transitioned out of lobby)
  const status = actualState.room_status || actualState.phase;
  const isGameStarted = status !== undefined && status !== 'lobby' && status !== 'LOBBY';
  const isOnGamePage = locationPathname.startsWith('/game/');
  const { sessionId } = store;

  logger.debug('ws_navigation_check', {
    status,
    isGameStarted,
    isOnGamePage,
    locationPathname,
    sessionId,
    willNavigate: isGameStarted && !isOnGamePage && sessionId,
  });

  if (isGameStarted && !isOnGamePage && sessionId) {
    logger.info('navigating_to_game', {
      from: locationPathname,
      sessionId,
      roomStatus: status,
    });
    navigate(`/game/${sessionId}`);
  }
}

function handleGameStateDelta(
  payload: GameStateDeltaPayload,
  requestResync: () => void,
  navigate: (path: string) => void,
  locationPathname: string
): void {
  const store = useGameStore.getState();

  if (!store.gameState) {
    logger.warn('websocket_delta_missing_base_state');
    requestResync();
    return;
  }

  if (
    !Array.isArray(payload.patch) ||
    typeof payload.sequence !== 'number' ||
    typeof payload.base_sequence !== 'number'
  ) {
    logger.warn('websocket_delta_invalid_payload');
    requestResync();
    return;
  }

  if (payload.base_sequence !== store.lastSeenSequence || payload.sequence <= payload.base_sequence) {
    logger.warn('websocket_delta_sequence_mismatch', {
      expectedBaseSequence: store.lastSeenSequence,
      receivedBaseSequence: payload.base_sequence,
      receivedSequence: payload.sequence,
    });
    requestResync();
    return;
  }

  try {
    const currentState = JSON.parse(JSON.stringify(store.gameState)) as Record<string, unknown>;
    const patchedState = applyPatch(
      currentState,
      payload.patch as unknown as Operation[],
      false,
      false
    ).newDocument;
    handleGameStateUpdate(
      {
        state: patchedState as unknown as GameState | LobbyState,
        sequence: payload.sequence,
        type: 'full',
      },
      navigate,
      locationPathname
    );
  } catch (error) {
    logger.warn('websocket_delta_apply_failed', { error: String(error) });
    requestResync();
  }
}

function handlePlayerJoined(payload: ServerPayloadMap['PLAYER_JOINED_WS']): void {
  const store = useGameStore.getState();
  store.addPlayer(payload as Parameters<typeof store.addPlayer>[0]);
}

function handlePlayerLeft(payload: ServerPayloadMap['PLAYER_LEFT_WS']): void {
  const store = useGameStore.getState();
  store.removePlayer(payload.player_id);
}

function handlePlayerProfileUpdated(payload: ServerPayloadMap['PLAYER_PROFILE_UPDATED']): void {
  const store = useGameStore.getState();
  store.updatePlayerProfile(payload.player_id, {
    profile_emoji: payload.profile_emoji,
    theme_primary_color: payload.theme_primary_color,
  });
}

function handleSpectatorJoined(payload: ServerPayloadMap['SPECTATOR_JOINED']): void {
  const store = useGameStore.getState();
  store.addSpectator(payload as Parameters<typeof store.addSpectator>[0]);
}

function handleSpectatorLeft(payload: ServerPayloadMap['SPECTATOR_LEFT']): void {
  const store = useGameStore.getState();
  store.removeSpectator(payload.player_id);
}

function handleSpectatorModeChanged(payload: ServerPayloadMap['SPECTATOR_MODE_CHANGED']): void {
  const store = useGameStore.getState();
  store.setAllowSpectators(payload.allow_spectators);
}

function handleLobbySettingsUpdated(payload: ServerPayloadMap['LOBBY_SETTINGS_UPDATED']): void {
  const store = useGameStore.getState();
  const settings = payload.settings as Parameters<typeof store.updateLobbySettings>[0];
  const changes = payload.changes as Parameters<typeof store.updateLobbySettings>[1];

  store.updateLobbySettings(settings, changes);
  const settingsStore = useSettingsStore.getState();
  settingsStore.setSettings({
    ...(settingsStore.currentSettings ?? {}),
    ...settings,
  });

  if (payload.changed_by && payload.changed_by === store.playerId) {
    toast.success('Settings saved');
  }
}

function handlePlayerClaimed(payload: ServerPayloadMap['PLAYER_CLAIMED']): void {
  const store = useGameStore.getState();
  store.updatePlayerNpcStatus(payload.player_id, {
    is_npc: false,
    display_name: payload.display_name,
  });
}

function handlePlayerConvertedToNpc(payload: ServerPayloadMap['PLAYER_CONVERTED_TO_NPC']): void {
  const store = useGameStore.getState();
  store.updatePlayerNpcStatus(payload.player_id, {
    is_npc: true,
    display_name: payload.display_name,
    claim_code: payload.claim_code,
  });
}

function handleHostReassigned(payload: ServerPayloadMap['HOST_REASSIGNED']): void {
  const store = useGameStore.getState();

  toast.info(`Host changed to ${payload.new_host_name}`, {
    description: 'The previous host disconnected',
  });

  store.reorderPlayersForHost(payload.new_host_id, payload.new_host_id === store.playerId);
}

function handleRoomClosing(payload: ServerPayloadMap['ROOM_CLOSING']): void {
  const store = useGameStore.getState();
  const reason = payload.reason || 'Room is closing';
  toast.warning('Room is closing', { description: reason });
  store.setSessionError(`Room closing: ${reason}`);
}

function handleErrorMessage(payload: ServerPayloadMap['ERROR_MESSAGE']): void {
  const store = useGameStore.getState();
  const errorDetail = payload.detail || 'An error occurred';

  if (
    errorDetail.includes('terminated by administrator') ||
    errorDetail.includes('cleared by administrator') ||
    errorDetail.includes('Session not found')
  ) {
    store.setSessionError(errorDetail);
  }

  window.dispatchEvent(
    new CustomEvent('ws-action-error', {
      detail: { error: errorDetail },
    })
  );
}

function handleJudgeForfeited(
  payload: ServerPayloadMap['JUDGE_FORFEITED'],
  navigate: (path: string) => void,
  locationPathname: string
): void {
  const newJudgeName = (payload as Record<string, unknown>).new_judge_name as string;
  const reason =
    ((payload as Record<string, unknown>).reason as string) || 'Psychiatrist disconnected';
  toast.info(`New Psychiatrist: ${newJudgeName}`, {
    description: reason,
    duration: 5000,
  });

  // If the payload contains game state fields, propagate as a state update
  const p = payload as Record<string, unknown>;
  if (p.room_status || p.all_players || p.current_round) {
    handleGameStateUpdate(payload as Record<string, unknown>, navigate, locationPathname);
  }
}

function handleSpectatorCountUpdate(payload: ServerPayloadMap['SPECTATOR_COUNT_UPDATE']): void {
  const count = (payload as Record<string, unknown>).count as number;
  useSpectatorStore.getState().setSpectatorCount(count);
}

// --- Hook ---

export function useMessageHandlers({
  navigate,
  getLocationPathname,
  requestResync,
}: UseMessageHandlersParams): (message: WebSocketMessage) => void {
  const { handleChatMessage, isChatAction } = useChatHandler();

  const handleMessage = useCallback(
    (message: WebSocketMessage) => {
      if (isChatAction(message.action)) {
        handleChatMessage(message);
        return;
      }

      const { action, payload } = message;

      switch (action) {
        case 'GAME_STATE_UPDATE':
          handleGameStateUpdate(payload, navigate, getLocationPathname());
          break;
        case 'GAME_STATE_DELTA':
          handleGameStateDelta(
            payload as unknown as GameStateDeltaPayload,
            requestResync,
            navigate,
            getLocationPathname()
          );
          break;
        case 'PLAYER_JOINED_WS':
          handlePlayerJoined(payload as ServerPayloadMap['PLAYER_JOINED_WS']);
          break;
        case 'PLAYER_LEFT_WS':
          handlePlayerLeft(payload as ServerPayloadMap['PLAYER_LEFT_WS']);
          break;
        case 'PLAYER_PROFILE_UPDATED':
          handlePlayerProfileUpdated(payload as ServerPayloadMap['PLAYER_PROFILE_UPDATED']);
          break;
        case 'PLAYER_CLAIMED':
          handlePlayerClaimed(payload as ServerPayloadMap['PLAYER_CLAIMED']);
          break;
        case 'PLAYER_CONVERTED_TO_NPC':
          handlePlayerConvertedToNpc(payload as ServerPayloadMap['PLAYER_CONVERTED_TO_NPC']);
          break;
        case 'SPECTATOR_JOINED':
          handleSpectatorJoined(payload as ServerPayloadMap['SPECTATOR_JOINED']);
          break;
        case 'SPECTATOR_LEFT':
          handleSpectatorLeft(payload as ServerPayloadMap['SPECTATOR_LEFT']);
          break;
        case 'SPECTATOR_MODE_CHANGED':
          handleSpectatorModeChanged(payload as ServerPayloadMap['SPECTATOR_MODE_CHANGED']);
          break;
        case 'LOBBY_SETTINGS_UPDATED':
          handleLobbySettingsUpdated(payload as ServerPayloadMap['LOBBY_SETTINGS_UPDATED']);
          break;
        case 'HOST_REASSIGNED':
          handleHostReassigned(payload as ServerPayloadMap['HOST_REASSIGNED']);
          break;
        case 'ROOM_CLOSING':
          handleRoomClosing(payload as ServerPayloadMap['ROOM_CLOSING']);
          break;
        case 'ERROR_MESSAGE':
          handleErrorMessage(payload as ServerPayloadMap['ERROR_MESSAGE']);
          break;
        case 'JUDGE_FORFEITED':
          handleJudgeForfeited(
            payload as ServerPayloadMap['JUDGE_FORFEITED'],
            navigate,
            getLocationPathname()
          );
          break;
        case 'SPECTATOR_COUNT_UPDATE':
          handleSpectatorCountUpdate(payload as ServerPayloadMap['SPECTATOR_COUNT_UPDATE']);
          break;
        case 'TIMER_START':
          useTimerStore.getState().handleTimerStart(payload as unknown as TimerStartPayload);
          break;
        case 'TIMER_TICK':
          useTimerStore.getState().handleTimerTick(payload as unknown as TimerTickPayload);
          break;
        case 'TIMER_COMPLETE':
          useTimerStore.getState().handleTimerComplete(payload as unknown as TimerCompletePayload);
          break;
        case 'TIMER_PAUSED':
          useTimerStore.getState().handleTimerPaused(payload as unknown as TimerPausedPayload);
          break;
        case 'TIMER_RESUMED':
          useTimerStore.getState().handleTimerResumed(payload as unknown as TimerResumedPayload);
          break;
        case 'TIMER_CANCELLED':
          useTimerStore.getState().handleTimerCancelled(payload as unknown as TimerCancelledPayload);
          break;
        default:
          // Child components handle other messages via store subscriptions or custom hooks
          break;
      }
    },
    [isChatAction, handleChatMessage, navigate, getLocationPathname, requestResync]
  );

  return handleMessage;
}
